import React, { FC } from 'react';
import { Button, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';

import { CustomSVG } from 'components';
import { ChatChannelPicker } from 'components/Pickers';
import { SharingChannelInfo, ContentInput, Network } from 'api';
import { useCurrentUser, useFeatures, useIntegrations } from 'hooks';
import { connectionPopup } from 'helpers';

import { usePostContext } from '../../PostContext';
import Widget from './WidgetContainer';
import { Switch } from '../';
import { isEligibleForImmediateShare } from '../../helpers';

const useStyles = makeStyles(theme => ({
  button: {
    padding: 0,
    float: 'right',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}));

const MicrosoftTeams: FC = () => {
  const { state, dispatch } = usePostContext();
  const { t } = useTranslation();
  const { legacyAuthDomain } = useFeatures();
  const { values, setFieldValue } = useFormikContext<ContentInput>();
  const classes = useStyles();
  const currentUser = useCurrentUser();
  const integrations = useIntegrations();
  const hasMSTeams = integrations.some(integration => integration.provider === Network.Msteams);

  if (!values.content_id && !isEligibleForImmediateShare(values)) {
    return null;
  }

  function handleConnect() {
    connectionPopup(currentUser.id, Network.Msteams, legacyAuthDomain);
  }

  function handleSwitch() {
    dispatch({ type: 'msteams', value: !state.msteams });
  }

  function handleChannelSelect(channels: SharingChannelInfo[]) {
    setFieldValue('ms_teams_channels', channels);
  }

  function renderBody() {
    return (
      <Grid container>
        <Grid item xs={1} />
        <Grid item xs={10}>
          <ChatChannelPicker
            network={Network.Msteams}
            selectedChannels={values.ms_teams_channels}
            onChannelChange={handleChannelSelect}
          />
        </Grid>
      </Grid>
    );
  }

  function renderToggle() {
    if (hasMSTeams) {
      return <Switch checked={state.msteams} onChange={handleSwitch} color="primary" />;
    }

    return (
      <Button color="primary" className={classes.button} onClick={handleConnect}>
        {t('user:connectTeamsAccount')}
      </Button>
    );
  }

  return (
    <Grid item xs>
      <Widget
        titleKey="posting:shareMicrosoftTeams"
        subtitleKey="posting:guidelineMicrosoftTeams"
        icon={<CustomSVG name={state.msteams ? 'msteams' : 'msteamsGreyscale'} />}
        toggle={renderToggle()}
        toggleColumns={hasMSTeams ? 1 : 3}>
        {state.msteams && renderBody()}
      </Widget>
    </Grid>
  );
};

export default MicrosoftTeams;
